import React from 'react';

// "Ledger & Seal" family, Predictive Restock & Dynamic Offer
// Personalization edition -- shares the shell used by
// DynamicPricingOnePager, RFPResponseOnePager, and AgenticReadinessOnePager.
// No title/video here -- heroBanner: true handles that (see useCases.js),
// with VideoPlaceholder standing in for the video until a real walkthrough
// exists.
const PALETTE = {
  blueLight: '#60a5fa',
  blue: '#1d4ed8',
  indigo: '#4f46e5',
  violet: '#6d28d9',
  pink: '#9d174d',
};

const stampGradient = `linear-gradient(120deg, ${PALETTE.blueLight} 0%, ${PALETTE.blue} 40%, ${PALETTE.indigo} 70%, ${PALETTE.violet} 100%)`;

const LEDGER_COLORS = ['#60a5fa', '#2563eb', '#4338ca', '#7c3aed', '#9333ea'];

const checklist = [
  { title: 'Predictive depletion modeling', body: 'Tracks product lifecycles and flags depletion before the cycle ends.' },
  { title: 'Forecasting at scale', body: 'SageMaker Pipelines run time-series models across millions of profiles.' },
  { title: 'Event-driven triggers', body: 'Day six of a seven-day milk cycle publishes a restock event to EventBridge.' },
  { title: 'Inventory-aware substitution', body: 'Constructor swaps an out-of-stock brand for the closest personalized alternative.' },
  { title: 'Margin-optimized offers', body: 'Bundles the reminder with a high-margin private label or complementary cross-sell.' },
  { title: 'One-click basket conversion', body: 'The notification deep-links into a pre-filled cart with other items due.' },
];

const stamps = [
  { num: '01', title: 'Purchases stream in', body: 'Loyalty scans and app orders flow in as they happen' },
  { num: '02', title: 'Depletion predicted', body: 'Forecasting models pinpoint the day each item runs out' },
  { num: '03', title: 'Offer assembled', body: 'Inventory checked, margin rules applied, substitutes chosen' },
  { num: '04', title: 'Cart pre-filled', body: 'One tap opens the app with the restock already in the basket' },
];

const ledgerRows = [
  { label: 'Increase in shopping frequency', value: '15-22%' },
  { label: 'Growth in basket size', value: '20%' },
  { label: 'The restock event fires before the cycle ends', value: 'Day 6 of 7' },
  { label: 'AWS, Constructor, and Braze in one loop', value: '3 layers' },
];

const PredictiveRestockOnePager = () => {
  return (
    <div className="w-full rounded-2xl bg-[#faf6ee] p-4 sm:p-6">
      <div
        className="relative mb-8 overflow-hidden rounded-md border border-[#e7dcc8] bg-[#fffdf8] p-6 pl-7 shadow-sm"
        style={{ borderLeft: `5px solid ${PALETTE.blueLight}` }}
      >
        <div className="mb-4 text-xs font-bold uppercase tracking-widest" style={{ color: PALETTE.indigo }}>
          Business Case Summary
        </div>

        <div className="mb-4">
          <h4 className="text-xl text-gray-900">The Challenge</h4>
          <p className="mt-1 text-base italic text-gray-500">Restocking runs on guesswork, and guesswork loses baskets.</p>
          <p className="mt-2 text-base leading-relaxed text-gray-700">
            Most restock outreach is scheduled, not predicted, using a monthly email blast, a generic push notification, or a static reminder. None of it knows a specific household is about to run out of coffee tomorrow morning. It's not that stores don't want to reach out sooner, it's that predicting thousands of individual consumption cycles by hand simply isn't possible. The result: quietly lost trips, baskets that migrate to a faster competitor, and reminders so generic shoppers tune them out.
          </p>
        </div>

        <hr className="my-4 border-dashed border-[#e7dcc8]" />

        <div>
          <h4 className="text-xl text-gray-900">The Outcome</h4>
          <p className="mt-1 text-base italic text-gray-500">Restock reminders that know before the shopper does.</p>
          <p className="mt-2 text-base leading-relaxed text-gray-700">
            Instead of a fixed schedule, the system tracks how fast each household actually goes through everyday items and reaches out right before they run out. It checks what's in stock nearby, picks a relevant substitute if needed, and hands them a cart that's already filled. The same logic scales to every regular in the store at once, giving a smaller discount to shoppers who'd buy anyway and a bigger one to those at risk of drifting to a competitor, so margin stays protected instead of eroded by a flat coupon. Managers stay hands-on too, asking who's due and setting discount rules through Amazon Quick, all in plain language.
          </p>
        </div>
      </div>

      <h3 className="mb-4 border-b-2 border-gray-900 pb-2 text-2xl text-gray-900">What the solution does</h3>
      <div className="mb-8 overflow-hidden rounded-md border border-[#e7dcc8] bg-[#fffdf8]">
        {checklist.map((item, i) => (
          <div
            key={item.title}
            className={`flex items-start gap-4 px-5 py-4 ${i !== checklist.length - 1 ? 'border-b border-[#f1e9d8]' : ''}`}
          >
            <svg
              className="mt-1.5 h-5 w-5 shrink-0"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke={PALETTE.blue}
              strokeWidth="3"
              aria-hidden="true"
            >
              <path d="M20 6 9 17l-5-5" />
            </svg>
            <div>
              <h4 className="text-lg font-semibold text-gray-900">{item.title}</h4>
              <p className="mt-1 text-base text-gray-500">{item.body}</p>
            </div>
          </div>
        ))}
      </div>

      <h3 className="mb-4 border-b-2 border-gray-900 pb-2 text-2xl text-gray-900">How it works</h3>
      <div className="relative mb-8 rounded-md border border-[#e7dcc8] bg-[#fffdf8] p-7">
        <div
          className="absolute left-[6%] right-[6%] top-[59px] hidden h-[2px] sm:block"
          style={{ background: stampGradient, opacity: 0.35 }}
          aria-hidden="true"
        />
        <div className="relative grid grid-cols-1 gap-6 sm:grid-cols-4">
          {stamps.map((stamp, i) => (
            <div key={stamp.num} className="text-center">
              <div
                className="mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-full text-xl font-extrabold text-white"
                style={{
                  background: `linear-gradient(135deg, ${
                    [PALETTE.blueLight, PALETTE.blue, PALETTE.indigo, PALETTE.violet][i]
                  }, ${[PALETTE.blue, PALETTE.indigo, PALETTE.violet, PALETTE.violet][i]})`,
                }}
              >
                {stamp.num}
              </div>
              <h4 className="text-lg font-semibold text-gray-900">{stamp.title}</h4>
              <p className="mt-1 text-base text-gray-500">{stamp.body}</p>
            </div>
          ))}
        </div>
        <p className="relative mt-6 border-t border-b-0 border-l-0 border-r-0 border-dashed border-[#e7dcc8] pt-4 text-base italic text-gray-500">
          Built on AWS with Amazon Kinesis, S3, SageMaker Pipelines, and EventBridge, plus Constructor for catalog intelligence and Braze for send-time orchestration.
        </p>
      </div>

      <h3 className="mb-4 border-b-2 border-gray-900 pb-2 text-2xl text-gray-900">
        Business Value Created
      </h3>
      <div className="grid grid-cols-1 rounded-md border border-[#e7dcc8] bg-[#fffdf8] sm:grid-cols-4">
        {ledgerRows.map((row, i) => (
          <div
            key={row.label}
            className={`flex flex-col items-center gap-1 px-4 py-5 text-center ${
              i !== ledgerRows.length - 1 ? 'border-b border-[#f1e9d8] sm:border-b-0 sm:border-r' : ''
            }`}
          >
            <span
              className="text-4xl font-extrabold"
              style={{
                background: `linear-gradient(135deg, ${LEDGER_COLORS[i]}, ${LEDGER_COLORS[i + 1]})`,
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                color: 'transparent',
              }}
            >
              {row.value}
            </span>
            <span className="text-base font-semibold text-gray-600">{row.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PredictiveRestockOnePager;
